import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, LoadScriptNext, InfoWindow, Marker } from "@react-google-maps/api";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import PageHeader from "./PageHeader";
import { useTheme } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import Spinner from "./Spinner";
import MobileHeader from './MobileHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faArrowDown, faArrowUp, faTimes, faMapMarkerAlt, faDollarSign, faRoute } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const googleMapsLibraries = ["places"];

const LocationFinderV2 = () => {
  const { currentUser } = useAuth();
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [radius, setRadius] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [sortCriteria, setSortCriteria] = useState({ field: 'price', ascending: true });
  const [userLocation, setUserLocation] = useState(null);
  const [actualUserLocation, setActualUserLocation] = useState(null);
  const [locationSource, setLocationSource] = useState(''); // 'geolocation', 'search', or 'ip'
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [mapZoom, setMapZoom] = useState(4);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const apiUrl = process.env.REACT_APP_API_URL;
  const ipInfoToken = 'ad22ffe4eb7ee0';
  const defaultMapStyles = [
    {
      featureType: "all",
      elementType: "labels.text.fill",
      stylers: [{ color: '#000000' }],
    },
    // Hide all POI (Points of Interest)
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    // Hide POI business labels
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }],
    },
    // Hide transit stations
    {
      featureType: "transit",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    // Keep water styling
    {
      "featureType": "water",
      "elementType": "geometry",
      "stylers": [
        { "color": "#b0e0e6" },
        { "lightness": 40 },
        { "saturation": -30 }
      ]
    },
  ];

  useEffect(() => {
    const requestUserLocation = () => {
      if (navigator.geolocation) {
        // Show prompt first
        setShowLocationPrompt(true);
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setActualUserLocation({ lat: latitude, lng: longitude });
            setUserLocation({ lat: latitude, lng: longitude });
            setLocationSource('geolocation');
            setShowLocationPrompt(false);
          },
          (error) => {
            console.log('Geolocation denied or failed, falling back to IP location');
            setShowLocationPrompt(false);
            fetchIPLocation();
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        console.log('Geolocation not supported, falling back to IP location');
        fetchIPLocation();
      }
    };

    const fetchIPLocation = async () => {
      try {
        const response = await axios.get(`https://ipinfo.io/json?token=${ipInfoToken}`);
        const [lat, lng] = response.data.loc.split(',');
        setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
        setLocationSource('ip');
      } catch (error) {
        console.error("Error getting user location from IPInfo:", error);
      }
    };

    requestUserLocation();
    
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const isValid = address.trim() !== "" && radius > 0 && radius <= 3500;
    setIsFormValid(isValid);
  }, [address, radius]);

  const handleSelect = async (value) => {
    setAddress(value);
    // Don't auto-geocode, wait for search button
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 3958.8; // Radius of the Earth in miles

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in miles
  };

  const fetchFuelStopsWithCoordinates = async (coordinatesToUse) => {
    if (radius > 3500) {
      alert("Radius should be 3500 or below.");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    const data = {
      latitude: coordinatesToUse.lat,
      longitude: coordinatesToUse.lng,
      searchRange: radius,
      UserId: currentUser.userId,
    };
    

    try {
      const response = await fetch(`${apiUrl}/getLocationsV2?`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error("Fetch failed with status:", response.status, response.statusText);
        setLoading(false);
        return;
      }

      const locationsData = await response.json();

      // Calculate distances using the coordinates we're searching from
      let distanceSource = coordinatesToUse;
      let distanceSourceName;
      
      // Determine what type of location we're using for distance
      if (coordinatesToUse === actualUserLocation && locationSource === 'geolocation') {
        distanceSourceName = 'your location';
      } else if (address) {
        distanceSourceName = address.split(',')[0]; // Just the city name
      } else {
        distanceSourceName = 'search location';
      }
      
      if (distanceSource) {
        locationsData.forEach((location) => {
          location.distance = calculateDistance(
            distanceSource.lat,
            distanceSource.lng,
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          ).toFixed(2);
          location.distanceSource = distanceSourceName;
        });
      }
      setLocations(locationsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fuel stops:", error);
      setLoading(false);
    }
  };

  const fetchFuelStops = async () => {
    if (radius > 3500) {
      alert("Radius should be 3500 or below.");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    let currentCoordinates = coordinates;


    if (!isValidCoordinates && address) {
      try {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        setCoordinates(latLng);
        currentCoordinates = latLng;
        setMapCenter(latLng); // Update the map center
        setMapZoom(10); // Set the zoom level after search        
      } catch (error) {
        console.error("Error geocoding address:", error);
        setLoading(false);
        return;
      }
    }
    
    if (!currentCoordinates || !currentCoordinates.lat || !currentCoordinates.lng) {
      setLoading(false);
      return;
    }

    const data = {
      latitude: currentCoordinates.lat,
      longitude: currentCoordinates.lng,
      searchRange: radius,
      UserId: currentUser.userId,
    };
    

    try {
      const response = await fetch(`${apiUrl}/getLocationsV2?`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error("Fetch failed with status:", response.status, response.statusText);
        setLoading(false);
        return;
      }

      const locationsData = await response.json();

      // Calculate distances - prioritize search location, fallback to actual GPS location
      let distanceSource, distanceSourceName;
      
      if (currentCoordinates && currentCoordinates.lat && currentCoordinates.lng) {
        // Use search location (most relevant for fuel search)
        distanceSource = currentCoordinates;
        distanceSourceName = address || 'search location';
      } else if (actualUserLocation && locationSource === 'geolocation') {
        // Fall back to user's actual GPS location (not IP)
        distanceSource = actualUserLocation;
        distanceSourceName = 'your location';
      } else {
        distanceSource = null;
        distanceSourceName = '';
      }
      
      if (distanceSource) {
        locationsData.forEach((location) => {
          location.distance = calculateDistance(
            distanceSource.lat,
            distanceSource.lng,
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          ).toFixed(2);
          location.distanceSource = distanceSourceName;
        });
      }
      setLocations(locationsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fuel stops:", error);
      setLoading(false);
    }
  };

  const defaultCenter = { lat: 39.8283, lng: -98.5795 };

  const isValidCoordinates =
    coordinates &&
    typeof coordinates.lat === "number" &&
    typeof coordinates.lng === "number";

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  const handleSort = (field) => {
    const isAscending = sortCriteria.field === field ? !sortCriteria.ascending : true;
    setSortCriteria({ field, ascending: isAscending });
  };

  const sortedLocations = [...locations].sort((a, b) => {
    let aValue, bValue;

    if (sortCriteria.field === 'price') {
      aValue = parseFloat(a.userPrice) || Infinity;
      bValue = parseFloat(b.userPrice) || Infinity;
    } else if (sortCriteria.field === 'distance') {
      aValue = parseFloat(a.distance) || Infinity;
      bValue = parseFloat(b.distance) || Infinity;
    }

    return sortCriteria.ascending ? aValue - bValue : bValue - aValue;
  });

  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const handleLocationClick = (location) => {
    if (isLargeScreen && mapInstance) {
      const lat = parseFloat(location.latitude);
      const lng = parseFloat(location.longitude);
      
      // Pan to location and zoom in
      mapInstance.panTo({ lat, lng });
      mapInstance.setZoom(15);
      
      // Set selected location to show info window
      setSelectedLocation(location);
    }
  };

  const renderMarkers = () => {
    return sortedLocations.map((location) => {
      if (!location.retailPrice || location.retailPrice === "NA") return null; // Skip if there's no price

      const markerIcon = theme.markerIcon ? {
        url: theme.markerIcon,
        labelOrigin: new window.google.maps.Point(15, 45),
        scaledSize: new window.google.maps.Size(30, 30)
      } : {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
            <path fill="${theme.primary}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
          </svg>
        `)}`,
        labelOrigin: new window.google.maps.Point(12, 45),
        scaledSize: new window.google.maps.Size(30, 30)
      };

      return (
        <Marker
          key={`marker-${location.locationID}`}
          position={{
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
          }}
          icon={markerIcon}
          onClick={() => setSelectedLocation(location)}
          label={{
            text: location.userPrice === "TBD" ? `$${parseFloat(location.retailPrice).toFixed(3)}` : `$${parseFloat(location.userPrice).toFixed(3)}`,
            color: "#000000",
            fontSize: "14px",
            fontWeight: "bold",
            className: "price-label",
            background: theme.primary, // Add background color
            borderRadius: "12px", // Add border-radius for pill effect
            padding: "2px 4px" // Add padding for pill effect
          }}
        />
      );
    });
  };

  const renderInfoWindow = () => {
    if (!selectedLocation) return null;

    const savings = (parseFloat(selectedLocation.retailPrice) - parseFloat(selectedLocation.userPrice)).toFixed(2);
    const price = isNaN(parseFloat(selectedLocation.userPrice)) ? "$0.00" : `$${parseFloat(selectedLocation.userPrice).toFixed(3)}`;
    const retailPrice = isNaN(parseFloat(selectedLocation.retailPrice)) ? "N/A" : `$${parseFloat(selectedLocation.retailPrice).toFixed(3)}`;
    const discount = isNaN(savings) ? "Pricing Unavailable" : `$${savings} Off`;
    const distance = selectedLocation.distance ? `${selectedLocation.distance}` : "";
    const modifiedOn = new Date(selectedLocation.modifiedOn).toLocaleString();

    return (
      <InfoWindow
        position={{
          lat: parseFloat(selectedLocation.latitude),
          lng: parseFloat(selectedLocation.longitude),
        }}
        onCloseClick={() => setSelectedLocation(null)}
        options={{ 
          pixelOffset: new window.google.maps.Size(0, -40),
          disableAutoPan: false
        }}
      >
        <div className="bg-white rounded-2xl shadow-xl border-0 overflow-hidden" style={{ maxWidth: '340px', minWidth: '300px' }}>
          {/* Header with gradient */}
          <div 
            className="px-5 py-5 text-white relative overflow-hidden"
            style={{ 
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primary}dd 50%, ${theme.primary}bb 100%)` 
            }}
          >
            <div className="relative z-10">
              <h3 className="text-base font-bold mb-2 leading-snug">{selectedLocation.locationName}</h3>
              <div className="text-sm opacity-90 leading-relaxed">
                {selectedLocation.addr1}
              </div>
              <div className="text-sm opacity-90">
                {selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip}
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-white opacity-10 rounded-full -mr-8 -mt-8"></div>
          </div>

          {/* Price Section */}
          <div className="px-5 py-6">
            {/* Main Price Display */}
            <div className="text-center mb-6">
              <div className="text-3xl font-bold mb-2" style={{ color: theme.primary }}>
                {price}
              </div>
              <div className="hidden sm:block text-xs text-gray-500 uppercase tracking-wider font-medium mb-4">
                Your Discounted Price
              </div>
              
              {/* Savings & Retail in compact row */}
              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="text-gray-500">
                  <span className="line-through">{retailPrice}</span>
                  <div className="text-xs text-gray-400">Retail</div>
                </div>
                {!isNaN(savings) && savings > 0 && (
                  <div className="bg-green-50 text-green-700 px-3 py-1 rounded-lg text-sm font-semibold">
                    Save {discount}
                  </div>
                )}
              </div>
            </div>

            {/* Distance & Directions */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              {distance && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: theme.primary }} className="text-xs" />
                  <span>
                    {distance} miles{coordinates && coordinates.lat && address ? ` from ${address.split(',')[0]}` : ''}
                  </span>
                </div>
              )}
              
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedLocation.addr2 + ', ' + selectedLocation.city + ', ' + selectedLocation.state + ' ' + selectedLocation.zip)}${(actualUserLocation || userLocation) ? `&origin=${(actualUserLocation || userLocation).lat},${(actualUserLocation || userLocation).lng}` : ''}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg"
                style={{ backgroundColor: theme.primary }}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-sm" />
                Get Directions
              </a>
            </div>

            {/* Footer */}
            <div className="mt-5 pt-4 border-t border-gray-100 text-center">
              <div className="text-xs text-gray-400">
                Updated {new Date(selectedLocation.modifiedOn).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </InfoWindow>
    );
  };

  return (
    <>
      <MobileHeader theme={theme} />
      <div className="mobile-container">
        <PageHeader
          heading="Location Finder"
          context="Search for fuel stops based on city and state. Customize the search radius and view the results."
          icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
        />
        
        {loading && <Spinner />}

        {/* Location Permission Prompt */}
        {showLocationPrompt && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: theme.primary }} className="text-lg" />
            <div className="flex-1">
              <div className="text-sm font-medium text-blue-800">
                Enable Location Access
              </div>
              <div className="text-xs text-blue-600 mt-1">
                Allow location access for accurate distance calculations to fuel stations
              </div>
            </div>
          </div>
        )}

        {/* Search Form - Redesigned */}
        <div className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <LoadScriptNext
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            libraries={googleMapsLibraries}
          >
            <div className="space-y-4">
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">Find Fuel Stations</h2>
                <p className="text-gray-600 text-sm">Enter your location and search radius to discover nearby fuel stations</p>
              </div>

              {/* Search Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                {/* Location Input */}
                <div className="md:col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <PlacesAutocomplete
                    value={address}
                    onChange={setAddress}
                    onSelect={handleSelect}
                    searchOptions={{
                      types: ['(cities)'],
                      componentRestrictions: { country: 'us' }
                    }}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <div className="relative">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: theme.primary }} className="h-5 w-5" />
                          </div>
                          <input
                            {...getInputProps({ placeholder: "Enter city or state" })}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            style={{ 
                              focusRingColor: theme.primary + '40',
                              '--tw-ring-color': theme.primary + '40'
                            }}
                            required
                          />
                        </div>
                        {(loading || suggestions.length > 0) && (
                          <div className="absolute inset-x-0 top-full bg-white border border-gray-200 rounded-xl shadow-xl z-20 mt-2 overflow-hidden">
                            {loading && <div className="p-4 text-gray-500 text-center">Searching...</div>}
                            {suggestions.map((suggestion) => (
                              <div
                                {...getSuggestionItemProps(suggestion)}
                                key={suggestion.placeId}
                                className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                                  {suggestion.description}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>

                {/* Radius Input */}
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Radius
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      max="3500"
                      value={radius}
                      onChange={(e) => {
                        if (e.target.value <= 3500) {
                          setRadius(e.target.value);
                        }
                      }}
                      placeholder="50"
                      className="w-full pr-16 pl-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                      style={{ 
                        focusRingColor: theme.primary + '40',
                        '--tw-ring-color': theme.primary + '40'
                      }}
                      required
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm font-medium">miles</span>
                    </div>
                  </div>
                </div>

                {/* Search Button */}
                <div className="md:col-span-2">
                  <button
                    onClick={fetchFuelStops}
                    disabled={!isFormValid}
                    className="w-full h-12 rounded-xl font-semibold text-white transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                    style={{ 
                      backgroundColor: isFormValid ? theme.primary : '#9CA3AF',
                      boxShadow: isFormValid ? `0 4px 20px ${theme.primary}40` : '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-sm" />
                      <span>Search</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Search Tips & Quick Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200 mt-6">
                <div className="hidden sm:flex items-center justify-center gap-6">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.primary }}></div>
                    <span>Your discounted prices</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: theme.primary }} className="text-xs" />
                    <span>Turn-by-turn directions</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">Max 3500 miles</span>
                  </div>
                </div>
                
                {actualUserLocation && locationSource === 'geolocation' && (
                  <button
                    onClick={() => {
                      setAddress('');
                      setCoordinates(actualUserLocation);
                      setMapCenter(actualUserLocation);
                      setMapZoom(10);
                      
                      // Call fetchFuelStops with the actual GPS location
                      if (radius && actualUserLocation) {
                        fetchFuelStopsWithCoordinates(actualUserLocation);
                      }
                    }}
                    disabled={!radius}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border"
                    style={{ 
                      borderColor: theme.primary + '40',
                      color: theme.primary,
                      backgroundColor: theme.primary + '10'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
                    <span>Use My Location</span>
                  </button>
                )}
              </div>
            </div>
          </LoadScriptNext>
        </div>

        {/* Mobile: List View Only, Desktop: Map + List */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Map - Hidden on mobile, visible on large screens */}
          <div className="hidden lg:block lg:flex-1">
            <LoadScriptNext
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              libraries={googleMapsLibraries}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <GoogleMap
                  onLoad={handleMapLoad}
                  center={mapCenter}
                  zoom={mapZoom}
                  mapContainerStyle={{ width: "100%", height: "600px" }}
                  options={{ 
                    styles: defaultMapStyles,
                    disableDefaultUI: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false
                  }}
                >
                  {renderMarkers()}
                  {renderInfoWindow()}
                </GoogleMap>
              </div>
            </LoadScriptNext>
          </div>

          {/* Locations List - Mobile friendly */}
          <div className="lg:w-1/3 w-full">
            {locations.length > 0 && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden lg:h-[600px] lg:flex lg:flex-col">
                {/* Header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h3 className="font-semibold text-gray-900">
                      {locations.length} Locations Found
                    </h3>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Sort:</span>
                      <button
                        onClick={() => handleSort('price')}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full transition-colors ${
                          sortCriteria.field === 'price' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        <FontAwesomeIcon icon={sortCriteria.field === 'price' && sortCriteria.ascending ? faArrowUp : faArrowDown} className="text-xs" />
                        Price
                      </button>
                      <button
                        onClick={() => handleSort('distance')}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full transition-colors ${
                          sortCriteria.field === 'distance' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        <FontAwesomeIcon icon={sortCriteria.field === 'distance' && sortCriteria.ascending ? faArrowUp : faArrowDown} className="text-xs" />
                        Distance
                      </button>
                    </div>
                  </div>
                </div>

                {/* Location Cards */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">
                  {sortedLocations.map((location, index) => {
                    const savings = (parseFloat(location.retailPrice) - parseFloat(location.userPrice)).toFixed(2);
                    const price = isNaN(parseFloat(location.userPrice)) ? "$0.00" : `$${parseFloat(location.userPrice).toFixed(3)}`;
                    const retailPrice = isNaN(parseFloat(location.retailPrice)) ? "N/A" : `$${parseFloat(location.retailPrice).toFixed(3)}`;
                    const discount = isNaN(savings) ? "Pricing Unavailable" : `$${savings} Off`;
                    const distance = location.distance ? `${location.distance}` : "";
                    const modifiedOn = new Date(location.modifiedOn).toLocaleString();

                    return (
                      <div 
                        key={location.locationID}
                        className={`p-3 ${index !== sortedLocations.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50 transition-colors ${
                          isLargeScreen ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => handleLocationClick(location)}
                      >
                        {/* Mobile-First Design */}
                        <div className="flex items-start gap-3">
                          {/* Left: Compact Price */}
                          <div className="flex-shrink-0">
                            <div 
                              className="w-16 h-16 rounded-lg flex flex-col items-center justify-center text-white text-center"
                              style={{ backgroundColor: theme.primary }}
                            >
                              <div className="text-lg font-bold leading-tight">
                                {price.replace('$', '')}
                              </div>
                              <div className="text-xs opacity-90">per gal</div>
                            </div>
                            {!isNaN(savings) && savings > 0 && (
                              <div className="text-center mt-1">
                                <div className="text-xs font-medium text-green-600">Save {discount}</div>
                              </div>
                            )}
                          </div>

                          {/* Right: Location Info */}
                          <div className="flex-1 min-w-0">
                            {/* Header Row */}
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight">{location.locationName}</h4>
                              {distance && (
                                <div className="flex items-center gap-1 text-xs text-gray-600 ml-2 flex-shrink-0">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" style={{ color: theme.primary }} />
                                  <span>{distance}mi{coordinates && coordinates.lat && address ? ` from ${address.split(',')[0]}` : ''}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Address */}
                            <div className="text-xs text-gray-600 mb-2 leading-relaxed">
                              <div>{location.addr1}</div>
                              <div>{location.city}, {location.state} {location.zip}</div>
                            </div>

                            {/* Bottom Row */}
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-gray-500">
                                Retail: {retailPrice}
                              </div>
                              
                              <a 
                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.addr2 + ', ' + location.city + ', ' + location.state + ' ' + location.zip)}${(actualUserLocation || userLocation) ? `&origin=${(actualUserLocation || userLocation).lat},${(actualUserLocation || userLocation).lng}` : ''}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded"
                                style={{ 
                                  color: theme.primary,
                                  backgroundColor: `${theme.primary}10`
                                }}
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
                                Directions
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer - Stick to bottom */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex-shrink-0 flex items-center">
                  <p className="text-xs text-gray-500 text-center">
                    *Prices may vary and are subject to change. We strive to provide accurate information, but discrepancies may occur.
                  </p>
                </div>
              </div>
            )}
            
            {/* Empty States */}
            {!hasSearched && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-6" style={{ color: theme.primary }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="opacity-50" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to Search</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto leading-relaxed">
                  Enter a city or address above and click search to find fuel stations in your area.
                </p>
                <div className="hidden sm:flex items-center justify-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.primary }}></div>
                    <span>Your discounted price</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: theme.primary }} />
                    <span>Turn-by-turn directions</span>
                  </div>
                </div>
              </div>
            )}
            
            {hasSearched && locations.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-6 text-gray-300">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Locations Found</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto leading-relaxed">
                  We couldn't find any fuel stations in your search area. Try expanding your search radius or searching a different location.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button 
                    onClick={() => setRadius(Math.min(parseInt(radius) + 50, 3500))}
                    className="px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors"
                    style={{ 
                      borderColor: theme.primary,
                      color: theme.primary,
                      backgroundColor: 'transparent'
                    }}
                    disabled={parseInt(radius) >= 3500}
                  >
                    Expand Radius (+50 miles)
                  </button>
                  <button 
                    onClick={() => {
                      setAddress('');
                      setHasSearched(false);
                      setLocations([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: theme.primary }}
                  >
                    Start New Search
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationFinderV2;
